import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AlertCircle, Loader2 } from 'lucide-react';

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation(); 
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    // Parse the hash fragment
    const hash = location.hash;
    if (!hash) {
      navigate('/auth');
      return;
    }

    // Parse the hash parameters
    const params = new URLSearchParams(hash.substring(1));
    const type = params.get('type');
    const token = params.get('access_token');

    if (!type || !token || type !== 'recovery') {
      setError('Invalid or expired recovery link');
      setTimeout(() => navigate('/auth'), 3000);
      return;
    }

    setAccessToken(token);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      if (!accessToken) {
        throw new Error('No access token found');
      }

      // Set the access token before updating password
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: ''
      });

      if (sessionError) throw sessionError;

      const { error } = await supabase.auth.updateUser({
        password: password,
        data: { last_password_reset: new Date().toISOString() }
      });

      if (error) throw error;

      setMessage('Password updated successfully. Redirecting to login...');
      setTimeout(() => {
        // Clear the session after password reset
        supabase.auth.signOut();
        navigate('/auth');
      }, 2000);
    } catch (err) {
      console.error('Error resetting password:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
      
      // If the error is related to an invalid token, redirect back to auth
      if (errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('session')) {
        setTimeout(() => navigate('/auth'), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">Create New Password</h1>
          <p className="text-gray-400 mt-2">Enter and confirm your new password below</p>
        </div>

        <div className="bg-[#2A2B2E] rounded-3xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Password</label>
              <input
                type="password"
                value={password}
                placeholder="Enter your new password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
                required
                minLength={6}
              />
              <p className="text-xs text-gray-400 mt-1">Must be at least 6 characters</p>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                placeholder="Confirm your new password"
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-xl text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {message && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-500 rounded-xl text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{message}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}